import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { observer } from 'mobx-react'
import { observable, action, toJS, makeObservable } from 'mobx'
import DropboxChooser from 'react-dropbox-chooser'
import classNames from 'classnames'

import { FILE_UPLOAD_DEFAULT_ACCEPTED_FILES, DROPBOX_APP_KEY, DROPBOX_EXTENSIONS } from 'constants/files.constants'
import { SIZES } from '@elo-kit/constants/general.constants'
import { LoadingSpinner } from '@elo-kit/components/loading-spinner/LoadingSpinner'
import { Ellipsis } from '@elo-kit/components/ellipsis/Ellipsis'
import CommunityFileSelectionModal from 'shared/components/community/components/file-selection-modal/CommunityFileSelectionModal'

import './community-file-upload-widget.scss'

const validateMediatheProps = (props, propName, componentName) => {
  const prop = props[propName]
  if (props.allowMediathek) {
    if (!prop) {
      return new Error(
        `'${propName}' prop is required if 'allowMediathek' prop is set to 'ture' in '${componentName}'.`
      )
    } else if (prop && typeof prop !== 'function') {
      return new Error(`'${propName}' prop should be a function in '${componentName}'.`)
    }
  }
}

const propTypes = {
  acceptedFiles: PropTypes.string,
  allowMediathek: PropTypes.bool,
  allowMultiple: PropTypes.bool,
  allowDropBox: PropTypes.bool,
  dropboxExtensions: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string.isRequired,
  fetchMediathekFiles: validateMediatheProps,
  saveMediathekFiles: validateMediatheProps,
  uploadFiles: PropTypes.func.isRequired,
  uploadDropboxFiles: PropTypes.func,
  mediathekModalTitle: PropTypes.string,
  loading: PropTypes.bool,
  filenameLimit: PropTypes.number,
  onInlineDelete: PropTypes.func,
}

const defaultProps = {
  acceptedFiles: FILE_UPLOAD_DEFAULT_ACCEPTED_FILES,
  allowMediathek: true,
  allowDropBox: true,
  allowMultiple: true,
  dropboxExtensions: DROPBOX_EXTENSIONS,
  loading: false,
  filenameLimit: 0,
}

@observer
class CommunityFileUploadWidget extends Component {
  @observable showMediathekModal = false

  @action toggleMediathekModal = () => {
    const { setQueryParams, onUploadClick } = this.props
    if (setQueryParams) {
      setQueryParams()
    }

    this.showMediathekModal = !this.showMediathekModal

    if (onUploadClick) {
      onUploadClick()
    }
  }

  constructor(props) {
    super(props)
    makeObservable(this)
  }

  render() {
    const {
      acceptedFiles,
      allowDropBox,
      allowMediathek,
      allowMultiple,
      className,
      disabled,
      dropboxExtensions,
      fetchMediathekFiles,
      good,
      id,
      initStore,
      mediathekFiles,
      mediathekModalTitle,
      onUploadClick,
      removingDocument,
      saveMediathekFiles,
      uploadDropboxFiles,
      uploadFiles,
      imageForPreview,
      showSeparator,
      formField,
      hideUpload,
      loading,
      fileName,
      fileIcon,
      coverId,
      shrinkWidth,
      imageDescription,
      filenameLimit,
      onInlineDelete,
      smallLoader,
      description,
      withImage,
    } = this.props

    const fieldClassNames = classNames(
      'community-file-upload-widget',
      {
        'community-file-upload-widget--form-field': formField,
        'community-file-upload-widget--separator-bottom': showSeparator,
        'community-file-upload-widget--disabled': disabled,
      },
      className
    )

    const fileUploadButtonsClasses = classNames('file-upload-buttons', {
      'file-upload-buttons--shrink': shrinkWidth,
      'file-upload-buttons--collapsed': imageForPreview,
    })

    const fileUploadButtonClasses = classNames('file-upload-buttons__button', {
      'file-upload-buttons__button--disabled': disabled,
    })
    const uploadContent = smallLoader ? <LoadingSpinner size={SIZES.extraSmall} /> : <i className='fas fa-upload' />

    const onInputClick = (event) => {
      event.target.value = ''
    }
    const UploadWrapper = imageDescription ? 'div' : Fragment
    const uploadWrapperProps = imageDescription ? { className: 'fields-container fields-container--top-align' } : {}

    const fileContent = fileName ? (
      <div className='fields-container--center-align'>
        {fileIcon && <i className={fileIcon} />}
        <i className='community-file-upload-widget__label community-file-upload-widget__label--extra'>
          {filenameLimit ? <Ellipsis maxLength={filenameLimit}>{fileName}</Ellipsis> : fileName}
        </i>
      </div>
    ) : (
      <div>
        {uploadContent}
        <div>{I18n.t('react.shared.button.from_device')}</div>
      </div>
    )

    return (
      <div className={fieldClassNames}>
        {description && withImage && <div className='community-file-upload-widget__description'>{description}</div>}
        {!hideUpload && (
          <UploadWrapper {...uploadWrapperProps}>
            <div className={fileUploadButtonsClasses} onClick={onUploadClick}>
              <Fragment>
                {loading ? (
                  <LoadingSpinner size={SIZES.small} />
                ) : (
                  <label htmlFor={`${id}-upload`} className={fileUploadButtonClasses}>
                    {!removingDocument && (
                      <input
                        accept={acceptedFiles}
                        className='file-upload-buttons__file-input--hidden'
                        disabled={disabled}
                        id={`${id}-upload`}
                        multiple={allowMultiple}
                        name={`${id}-upload`}
                        type='file'
                        onChange={({ target }) => uploadFiles(target.files)}
                        onClick={onInputClick}
                      />
                    )}
                    {fileContent}
                  </label>
                )}
              </Fragment>

              {onInlineDelete && fileName && (
                <button type='button' onClick={onInlineDelete} className='elo-editable-image__delete-btn'>
                  <i className='far fa-trash-alt' />
                </button>
              )}

              {allowMediathek && (
                <div className={fileUploadButtonClasses} onClick={this.toggleMediathekModal}>
                  <div>
                    <i className='fas fa-cloud-upload-alt' />
                    <div>{I18n.t('react.shared.button.from_library')}</div>
                  </div>
                </div>
              )}

              {allowDropBox && (
                <div className={fileUploadButtonClasses} onClick={onUploadClick}>
                  <DropboxChooser
                    appKey={DROPBOX_APP_KEY}
                    extensions={dropboxExtensions}
                    multiselect={allowMultiple}
                    success={(files) => uploadDropboxFiles(files)}
                    linkType='direct'
                  >
                    <i className='fab fa-dropbox' />
                    <div>{I18n.t('react.shared.button.from_dropbox')}</div>
                  </DropboxChooser>
                </div>
              )}

              {this.showMediathekModal && (
                <CommunityFileSelectionModal
                  toggle={this.toggleMediathekModal}
                  modal={this.showMediathekModal}
                  fetchFileList={fetchMediathekFiles}
                  saveFiles={saveMediathekFiles}
                  fileList={toJS(mediathekFiles)}
                  modalTitle={mediathekModalTitle}
                  {...{
                    allowMultiple,
                    good,
                    initStore,
                    coverId,
                  }}
                />
              )}
            </div>
          </UploadWrapper>
        )}
        {description && !withImage && <div className='community-file-upload-widget__description'>{description}</div>}
      </div>
    )
  }
}

CommunityFileUploadWidget.displayName = 'CommunityFileUploadWidget'
CommunityFileUploadWidget.propTypes = propTypes
CommunityFileUploadWidget.defaultProps = defaultProps

export default CommunityFileUploadWidget
